import type { ColumnDefWithMappedValue, DateFormatsType } from '@dolfin/utils/data-loader'
import type { MessageDescriptor } from '@lingui/core'
import type { Edge as ReactFlowEdge } from '@reactflow/core'
import type { ReactElement } from 'react'
import { z } from 'zod'
import {
  ActivitiesWithDeals,
  CustomRecordsWithTables,
  DealLineItemsWithJoins,
  GridTable,
  InvoicesWithDeals,
  KpisWithTables,
  LineItemsWithJoins
} from './custom_types'
import { CompositeTypes, Enums, Tables, TablesInsert } from './db_types'
import {
  AnyTypedNode,
  MetricRef,
  NodeFormDataMap,
  NodeId,
  NodeIdExcludingPlaceholder,
  TargetRef,
  TieredNodeId,
  TypedNode,
  TypedNodeData
} from './node/types'

/**
 * Common value types used across node outputs
 */
export type CompensationValue = {
  profile_id: string
  value: number
}

export type CompensationValueArray = CompensationValue[]

export type TargetOutcome = {
  profile_id: string
  outcome: number
}

export type TargetOutcomeArray = TargetOutcome[]

export type PreviousPayout = {
  profile_id: string
  value: number
}

export type PreviousPayoutArray = PreviousPayout[]

export type CompensationValueSource = 'long_term_balancing' | 'period'

export type CompensationValueSourceEntry = {
  profile_id: string
  value: CompensationValueSource
}

export type CompensationValueSourceArray = CompensationValueSourceEntry[]

export type BonusContextTarget = {
  attainment: number
  outcomes: TargetOutcomeArray
  target?: (TargetRef & { target_amount?: number }) | undefined
  targets?: Array<{
    attainment: number
    target: TargetRef & { target_amount?: number }
    period: {
      start: string
      end: string
    }
  }>
  period: {
    start: string
    end: string
  }
}

export type BonusContextMetric = {
  attainment: number
  metric: MetricRef
  period: {
    start: string
    end: string
  }
}

export type BonusContext = BonusContextTarget | BonusContextMetric

// Allow more flexible context values for runtime compatibility
export type BonusContextValue =
  | BonusContext
  | {
      attainment: number
      outcomes?: TargetOutcomeArray
      target?: unknown
      targets?: Array<{
        attainment: number
        target: TargetRef & { target_amount?: number }
        period: {
          start: string
          end: string
        }
      }>
      metric?: MetricRef
      period: {
        start: string
        end: string
      }
    }

export type CommissionContext = {
  attainment: number
  metric?: MetricRef
  period: {
    start: string
    end: string
  }
  tvp_based?: boolean
  tvp_allocation?: number
  tvp_allocation_before_proration?: number
  tvp_proration_factor?: number
  yearly_target?: number
  [key: string]: unknown // Allow additional properties for runtime flexibility
}

/**
 * Commission Node Output Types
 */
export type CommissionOutputField =
  | { name: 'commission_percentage'; value: number }
  | { name: 'compensation_value'; value: number }
  | { name: 'compensation_value_period_candidate'; value: number }
  | { name: 'field_value'; value: number }
  | { name: 'metric_value'; value: number }
  | { name: 'metric_currency'; value: string }
  | { name: 'tier_index'; value: number }
  | { name: 'attainment'; value: number }
  | {
      name: 'marginal_commission_tiering'
      value: Array<{
        tier_index: number
        attainment_start: number
        attainment_end: number
        field_value: number
        commission_percentage: number
        compensation_value: number
      }>
    }
  | { name: 'context'; value: CommissionContext }
  | { name: 'tvp_allocation'; value: number }
  | { name: 'tvp_allocation_before_proration'; value: number }
  | { name: 'tvp_proration_factor'; value: number }
  | { name: 'tvp_percentage'; value: number }
  | { name: 'tvp_total'; value: number }
  | { name: 'total_previous_payouts'; value: number }
  | { name: 'compensation_value_before_regulation'; value: number }
  | { name: 'compensation_value_comparison'; value: Record<string, unknown> }
  | { name: 'regulation_period'; value: string | null | undefined }
  | {
      name: 'compensation_value_source'
      value: 'long_term_balancing' | 'period'
    }
  | { name: 'yearly_target_amount'; value: number }

export type CommissionOutput = CommissionOutputField[]

/**
 * Bonus Node Output Types
 */
export type BonusOutputField =
  | { name: 'bonus_percentage'; value: number }
  | { name: 'compensation_value'; value: number }
  | { name: 'compensation_value_period_candidate'; value: number }
  | {
      name: 'compensation_value_source'
      value: CompensationValueSource | CompensationValueSourceArray
    }
  | { name: 'compensation_values'; value: CompensationValueArray }
  | {
      name: 'compensation_values_period_candidates'
      value: CompensationValueArray
    }
  | { name: 'target_outcomes'; value: TargetOutcomeArray }
  | { name: 'tier_index'; value: number }
  | { name: 'attainment'; value: number }
  | { name: 'context'; value: BonusContextValue }
  | { name: 'tvp_allocation'; value: number }
  | { name: 'tvp_percentage'; value: number }
  | { name: 'tvp_total'; value: number }
  | { name: 'total_previous_payouts'; value: PreviousPayoutArray | number }
  | { name: 'compensation_value_before_regulation'; value: number }
  | {
      name: 'compensation_values_before_regulation'
      value: CompensationValueArray
    }
  | { name: 'regulation_period'; value: string }

export type BonusOutput = BonusOutputField[]

/**
 * Addition Node Output Types
 */
export type AdditionOutputField =
  | { name: 'addition'; value: number }
  | { name: 'additions'; value: CompensationValueArray }
  | { name: 'compensation_value'; value: number }
  | { name: 'compensation_value_period_candidate'; value: number }
  | { name: 'compensation_values'; value: CompensationValueArray }
  | {
      name: 'compensation_values_period_candidates'
      value: CompensationValueArray
    }

export type AdditionOutput = AdditionOutputField[]

/**
 * Multiplier Node Output Types
 */
export type MultiplierOutputField =
  | { name: 'multiplier'; value: number }
  | { name: 'compensation_value'; value: number }
  | { name: 'compensation_value_period_candidate'; value: number }
  | { name: 'compensation_values'; value: CompensationValueArray }
  | {
      name: 'compensation_values_period_candidates'
      value: CompensationValueArray
    }
  | { name: 'tier_index'; value: number }
  | { name: 'attainment'; value: number }

export type MultiplierOutput = MultiplierOutputField[]

/**
 * Typed VariableOutput based on node type
 */
export type TypedVariableOutput<TNodeId extends NodeId> = TNodeId extends 'commission'
  ? CommissionOutput
  : TNodeId extends 'bonus'
    ? BonusOutput
    : TNodeId extends 'addition'
      ? AdditionOutput
      : TNodeId extends 'multiplier'
        ? MultiplierOutput
        : {
            name: string
            value: unknown
          }[]

/**
 * Legacy VariableOutput type (kept for backward compatibility)
 * Use TypedVariableOutput<TNodeId> for type-safe access
 */
export type VariableOutput = {
  name: string
  value: unknown
}[]

// =============================================================================
// Type-safe Output Value Extraction
// =============================================================================

/**
 * All possible output field types across all nodes
 */
export type AnyOutputField =
  | CommissionOutputField
  | BonusOutputField
  | AdditionOutputField
  | MultiplierOutputField

/**
 * Extract the value type for a specific field name from an output type.
 * Uses conditional type inference to get the exact value type.
 */
export type ExtractOutputValue<
  TOutput extends { name: string; value: unknown }[],
  TName extends string
> = Extract<TOutput[number], { name: TName }>['value']

/**
 * All possible field names for Commission output
 */
export type CommissionOutputFieldName = CommissionOutputField['name']

/**
 * All possible field names for Bonus output
 */
export type BonusOutputFieldName = BonusOutputField['name']

/**
 * All possible field names for Addition output
 */
export type AdditionOutputFieldName = AdditionOutputField['name']

/**
 * All possible field names for Multiplier output
 */
export type MultiplierOutputFieldName = MultiplierOutputField['name']

/**
 * Type-safe getter for Commission output values.
 * Returns the correctly typed value for the given field name.
 */
export function getCommissionOutputValue<TName extends CommissionOutputFieldName>(
  output: CommissionOutput,
  name: TName
): ExtractOutputValue<CommissionOutput, TName> | undefined {
  return output.find(o => o.name === name)?.value as
    | ExtractOutputValue<CommissionOutput, TName>
    | undefined
}

/**
 * Type-safe getter for Bonus output values.
 * Returns the correctly typed value for the given field name.
 */
export function getBonusOutputValue<TName extends BonusOutputFieldName>(
  output: BonusOutput,
  name: TName
): ExtractOutputValue<BonusOutput, TName> | undefined {
  return output.find(o => o.name === name)?.value as
    | ExtractOutputValue<BonusOutput, TName>
    | undefined
}

/**
 * Type-safe getter for Addition output values.
 * Returns the correctly typed value for the given field name.
 */
export function getAdditionOutputValue<TName extends AdditionOutputFieldName>(
  output: AdditionOutput,
  name: TName
): ExtractOutputValue<AdditionOutput, TName> | undefined {
  return output.find(o => o.name === name)?.value as
    | ExtractOutputValue<AdditionOutput, TName>
    | undefined
}

/**
 * Type-safe getter for Multiplier output values.
 * Returns the correctly typed value for the given field name.
 */
export function getMultiplierOutputValue<TName extends MultiplierOutputFieldName>(
  output: MultiplierOutput,
  name: TName
): ExtractOutputValue<MultiplierOutput, TName> | undefined {
  return output.find(o => o.name === name)?.value as
    | ExtractOutputValue<MultiplierOutput, TName>
    | undefined
}

export type PaymentNodeOutputElement = Omit<TablesInsert<'payouts'>, 'execution_id'> & {
  timing: string
  withheld: boolean
  phase_id: string
  payment_date?: string
  _exchangeInfo?: Omit<TablesInsert<'exchanges'>, 'payout_id'>
  payout_installments: (Omit<TablesInsert<'payout_installments'>, 'payout_id'> & {
    payment_date?: string
  })[]
}

export const isVariableOutput = <TNodeId extends NodeId>(
  output: NodeProcessOutput<TNodeId>
): output is TypedVariableOutput<TNodeId> => {
  return (
    typeof output === 'object' &&
    output !== null &&
    Array.isArray(output) &&
    output.length > 0 &&
    'name' in output[0] &&
    'value' in output[0]
  )
}

export type PaymentNodeOutput = PaymentNodeOutputElement[]

export type NodeOutputContext = {
  attainment: number
  periodStart: string
  periodEnd: string
  target_amount?: number
  outcomes?: {
    profile_id: string
    outcome: number
  }[]
}

export type NodeProcessOutput<TNodeId extends NodeId> =
  | number
  | boolean
  | PaymentNodeOutput
  | Omit<TablesInsert<'whistles'>, 'execution_id'>
  | TypedVariableOutput<TNodeId>
  | string
  | null

export enum EdgeType {
  Default = 'default',
  True = 'true',
  False = 'false',
  MultiPath = 'multi-path'
}

export type TierData = {
  threshold: number
  outcome_value: number
}

export type EdgeOptionDefinition = {
  type: EdgeType
}

export type CompensationNodeTypes = 'commission' | 'bonus' | 'payment' | 'multiplier' | 'addition'
export type BaseNodeTypes =
  | 'filter'
  | 'if/else'
  | 'multi-path'
  | 'branch-by-user'
  | 'start'
  | 'custom-check'
  | CompensationNodeTypes

// Re-export typed node types for convenience
export {
  hasCalculateOverColumn,
  hasCalculateOverMetric,
  // Addition node type guards
  isAdditionColumnBased,
  isAdditionMetricBased,
  isAdditionTargetBased,
  // Node category type guards
  isCalculationNode,
  isConditionNode,
  // Multiplier node type guards
  isMultiplierMetricBased,
  isMultiplierTargetBased,
  isNodeType,
  isTieredCommissionWithColumn,
  isTieredCommissionWithMetric,
  isTieredCommissionWithTargets,
  isTieredNode
} from './node/types'
export type {
  AdditionFormData,
  AdditionFormDataColumn,
  AdditionFormDataMetric,
  AdditionFormDataTarget,
  AnyTypedNodeData,
  BonusFormData,
  BonusFormDataMetric,
  BonusFormDataTarget,
  BranchByUserFormData,
  ColumnRef,
  CommissionFormData,
  DataCheckFormData,
  FilterFormData,
  IfElseFormData,
  MetricRef,
  MultiPathFormData,
  MultiplierFormData,
  MultiplierFormDataMetric,
  MultiplierFormDataTarget,
  NodeFormDataMap,
  NodeId,
  PaymentFormData,
  StartFormData,
  TargetRef,
  Tier,
  TypedNodeData
} from './node/types'

/**
 * Node option definition with loose typing.
 * @deprecated Use TypedNodeOptionDefinition for type-safe node definitions
 */
export type NodeOptionDefinition = {
  id: NodeId
  type: string
  formData: Record<string, unknown>
  requiredPreviousNodes?: NodeId[]
  requireAllPreviousNodes?: boolean
  unique?: boolean
  isLast?: boolean
  edges?: EdgeOptionDefinition[]
  label?: MessageDescriptor
  description?: MessageDescriptor
  icon?: ReactElement
  parsedValues?: Record<string, unknown>
}

/**
 * Typed node option definition with strict formData typing.
 * Use this for new code to get full type safety.
 */
export type TypedNodeOptionDefinition<T extends NodeId> = {
  id: T
  type: string
  formData: T extends keyof import('./node/types').NodeFormDataMap
    ? Partial<import('./node/types').NodeFormDataMap[T]>
    : Record<string, unknown>
  requiredPreviousNodes?: NodeId[]
  requireAllPreviousNodes?: boolean
  unique?: boolean
  isLast?: boolean
  edges?: EdgeOptionDefinition[]
  label?: MessageDescriptor
  description?: MessageDescriptor
  icon?: ReactElement
  parsedValues?: Record<string, unknown>
  color?: string
}

/**
 * Node data with loose typing.
 * @deprecated Use TypedNodeData for type-safe node data access
 */
export type NodeData = Omit<NodeOptionDefinition, 'icon'> & { type: BaseNodeTypes }

export type IEdgeData = {
  value?: string
  pathNumber?: number
}

export type Edge<T> = ReactFlowEdge<T> & {
  type: EdgeType
}

export const nodeOutputIsPayout = <TNodeId extends NodeId>(
  output: NodeProcessOutput<TNodeId>
): output is PaymentNodeOutput => {
  return (
    Array.isArray(output) &&
    output.length > 0 &&
    typeof output[0] === 'object' &&
    output[0] !== null &&
    'value' in output[0] &&
    'type' in output[0] &&
    'effective_date' in output[0]
  )
}

// =============================================================================
// Node Output Type Mapping
// =============================================================================

/**
 * Maps each node type to its corresponding output type.
 * Used for type-safe access to node outputs based on node type.
 */
export type NodeOutputMap = {
  commission: CommissionOutput
  bonus: BonusOutput
  addition: AdditionOutput
  multiplier: MultiplierOutput
  payment: PaymentNodeOutput
  filter: boolean
  'if/else': boolean
  'multi-path': number
  'branch-by-user': number
  start: boolean
  'custom-check':
    | boolean
    | {
        name: string
        value: unknown
      }[]
  'slack-alert': boolean
  email: boolean
}

/**
 * All node types that have typed outputs.
 * Identical to NodeId - both represent all valid node types.
 */
export type TypedNodeType = NodeId

/**
 * Get the output type for a specific node type
 */
export type OutputForNodeType<T extends NodeIdExcludingPlaceholder> = NodeOutputMap[T]

// =============================================================================
// Plan State Types
// =============================================================================

/**
 * Base plan state interface with common properties
 */
interface BasePlanState {
  nextNode?: string | null
  outputLabel?: string
  originalFormula?: string
  tierIndex?: number
  nextTierIndex?: number
}

/**
 * Typed plan state for a specific node type.
 * Links the currentNode type with its corresponding output type.
 *
 * @example
 * ```typescript
 * // Type-safe access to commission output
 * const state: TypedPlanState<'commission'> = getState()
 * const percentage = getCommissionOutputValue(state.nodeOutput, 'commission_percentage')
 * ```
 */
export interface TypedPlanState<T extends NodeIdExcludingPlaceholder> extends BasePlanState {
  currentNode: TypedNode<T>
  nodeOutput: NodeOutputMap[T]
}

/**
 * Plan state with null currentNode (for initial/error states)
 */
export interface NullPlanState extends BasePlanState {
  currentNode: null
  nodeOutput: NodeProcessOutput<never>
}

/**
 * Union of all possible typed plan states.
 * Automatically generated from NodeId to ensure consistency.
 * Use this type when you need type-safe access to node outputs.
 */
export type AnyTypedPlanState =
  | {
      [K in NodeIdExcludingPlaceholder]: TypedPlanState<K>
    }[NodeIdExcludingPlaceholder]
  | NullPlanState

/**
 * IPlanState interface.
 * Use type guards (isCommissionPlanState, isBonusPlanState, etc.) to narrow
 * to specific typed states for type-safe output access.
 */
export type IPlanState = TypedPlanState<NodeIdExcludingPlaceholder> | NullPlanState

// =============================================================================
// Plan State Type Guards
// =============================================================================

/**
 * Check if a plan state has a specific node type.
 * Use the specific type guards (isCommissionPlanState, etc.) for type narrowing.
 *
 * @example
 * ```typescript
 * if (isPlanStateForNode(state, 'commission')) {
 *   // Use isCommissionPlanState for type narrowing
 * }
 * ```
 */
export function isPlanStateForNode(state: IPlanState, nodeType: TypedNodeType): boolean {
  return state.currentNode?.type === nodeType
}

/**
 * Type guard to check if plan state is for a commission node
 */
export function isCommissionPlanState(state: IPlanState): state is TypedPlanState<'commission'> {
  return isPlanStateForNode(state, 'commission')
}

/**
 * Type guard to check if plan state is for a bonus node
 */
export function isBonusPlanState(state: IPlanState): state is TypedPlanState<'bonus'> {
  return isPlanStateForNode(state, 'bonus')
}

/**
 * Type guard to check if plan state is for an addition node
 */
export function isAdditionPlanState(state: IPlanState): state is TypedPlanState<'addition'> {
  return isPlanStateForNode(state, 'addition')
}

/**
 * Type guard to check if plan state is for a multiplier node
 */
export function isMultiplierPlanState(state: IPlanState): state is TypedPlanState<'multiplier'> {
  return isPlanStateForNode(state, 'multiplier')
}

/**
 * Type guard to check if plan state is for a payment node
 */
export function isPaymentPlanState(state: IPlanState): state is TypedPlanState<'payment'> {
  return isPlanStateForNode(state, 'payment')
}

export type TieredPlanState<T extends TieredNodeId> = Omit<TypedPlanState<T>, 'currentNode'> & {
  currentNode: T extends 'commission' | 'bonus'
    ? Extract<
        TypedNode<T>,
        { data: TypedNodeData<T> & { formData: Extract<NodeFormDataMap[T], { type: 'tiered' }> } }
      >
    : TypedNode<'multiplier'>
}

/**
 *
 * Type guard to check if plan state is for a tiered node
 */
export function isTieredPlanState(state: IPlanState): state is TieredPlanState<TieredNodeId> {
  return isCommissionPlanState(state) || isBonusPlanState(state) || isMultiplierPlanState(state)
}

export type JsonCreatorVariableInfo = {
  id: string
  value: string | number
}
export type JsonCreatorVariable = Record<string, JsonCreatorVariableInfo>

export type NodeError = {
  message: string
  node?: AnyTypedNode
}
export type NodeWarning = NodeError

export type RecordData =
  | Omit<Tables<'deals'>, 'deals_search_columns'>
  | ActivitiesWithDeals
  | InvoicesWithDeals
  | CustomRecordsWithTables
  | Omit<Tables<'kpis'>, 'kpis_search_columns'>
  | KpisWithTables
  | LineItemsWithJoins
  | DealLineItemsWithJoins

export type ParsedExecutionResult = {
  frozen?: boolean
  entityId?: number
  sourceUpdatedAt?: string
  attributionKey?: string
  organizationId: number
  ruleId?: number
  ruleVersionId?: number
  periodStart?: string
  periodEnd?: string
  profileId?: string
  recalculationReason?: string
  payouts: PayoutsToInsert[]
  path?: IPlanState[]
  calculationContext?: CalculationContext
  clawbacks?: TablesInsert<'clawbacks'>[]
}

export type CalculationSnapshotSourceField = {
  field: string
  value: string | number | boolean | string[] | null
  label?: string
  type?: string
  currency?: string
}

export type CalculationContextMetricInput = {
  kind: 'metric'
  nodeId: string
  metric: MetricRef
  value: number
  attainment: number
  period: { start: string; end: string }
  attainmentPeriod?: { start: string; end: string }
  currency?: string
  count?: number
}

export type CalculationContextTargetInput = {
  kind: 'target'
  nodeId: string
  target: TargetRef
  value: number
  attainment: number
  targetAmount?: number
  period: { start: string; end: string }
  attainmentPeriod?: { start: string; end: string }
  currency?: string
  count?: number
}

export type CalculationContextInput = CalculationContextMetricInput | CalculationContextTargetInput

export type CalculationContext = {
  schemaVersion: 1
  kind: 'record' | 'period'
  calculatedAt: string
  rule: {
    id: number
    label: string
    versionId: number
    activePeriod: string | null
  }
  referenceDate?: string
  period?: { start: string; end: string }
  source?: {
    type: Enums<'plan_source'>
    id: number
    fields: CalculationSnapshotSourceField[]
  }
  user: {
    profileId: string
    type: string | null
    totalVariablePay: number | null
    totalVariablePayCurrency?: string
    totalVariablePayCurrencyInferred?: boolean
    teamId: number | null
    teamName?: string | null
    scopeTeamIds: number[]
  }
  selectedPaymentOutput?: PaymentNodeOutput
  selectedPaymentOutputNodeId?: string
  inputs?: CalculationContextInput[]
}

export const CalculationContextSchema = z.object({
  schemaVersion: z.literal(1),
  kind: z.enum(['record', 'period']),
  calculatedAt: z.string().datetime(),
  rule: z.object({
    id: z.number().int().positive(),
    label: z.string(),
    versionId: z.number().int().positive(),
    activePeriod: z.string().nullable()
  }),
  referenceDate: z.string().optional(),
  period: z.object({ start: z.string(), end: z.string() }).optional(),
  source: z
    .object({
      type: z.enum([
        'deal',
        'activity',
        'invoice',
        'kpi',
        'line_item',
        'deal_line_item',
        'custom_record'
      ]),
      id: z.number().int().positive(),
      fields: z.array(
        z.object({
          field: z.string(),
          value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]),
          label: z.string().optional(),
          type: z.string().optional(),
          currency: z.string().optional()
        })
      )
    })
    .optional(),
  user: z.object({
    profileId: z.string().uuid(),
    type: z.string().nullable(),
    totalVariablePay: z.number().nullable(),
    totalVariablePayCurrency: z.string().optional(),
    totalVariablePayCurrencyInferred: z.boolean().optional(),
    teamId: z.number().int().positive().nullable(),
    teamName: z.string().nullable().optional(),
    scopeTeamIds: z.array(z.number().int().positive())
  }),
  selectedPaymentOutput: z
    .custom<PaymentNodeOutput>(value => nodeOutputIsPayout(value as NodeProcessOutput<NodeId>))
    .optional(),
  selectedPaymentOutputNodeId: z.string().optional(),
  inputs: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({
          kind: z.literal('metric'),
          nodeId: z.string(),
          metric: z.object({
            id: z.number(),
            credit_setup_id: z.number().int().positive().nullable().optional(),
            label: z.string().optional(),
            description: z.string().nullable().optional(),
            type: z.enum(['monetary', 'numeric', 'percentage', 'count']).optional(),
            source: z
              .enum([
                'deal',
                'activity',
                'invoice',
                'kpi',
                'line_item',
                'deal_line_item',
                'custom_record'
              ])
              .nullable()
              .optional(),
            aggregate_function: z
              .enum(['sum', 'average', 'count', 'min', 'max', 'latest'])
              .optional()
          }),
          value: z.number(),
          attainment: z.number(),
          period: z.object({ start: z.string(), end: z.string() }),
          attainmentPeriod: z.object({ start: z.string(), end: z.string() }).optional(),
          currency: z.string().optional(),
          count: z.number().int().nonnegative().optional()
        }),
        z.object({
          kind: z.literal('target'),
          nodeId: z.string(),
          target: z.object({
            id: z.number(),
            name: z.string().optional(),
            label: z.string().optional(),
            team_target: z.boolean(),
            company_target: z.boolean().optional(),
            weight: z.number().optional(),
            period_candidate_target_id: z.number().optional(),
            global_target: z.boolean().optional(),
            teams_targets: z.array(z.object({ team_id: z.number() })).optional()
          }),
          value: z.number(),
          attainment: z.number(),
          targetAmount: z.number().optional(),
          period: z.object({ start: z.string(), end: z.string() }),
          attainmentPeriod: z.object({ start: z.string(), end: z.string() }).optional(),
          currency: z.string().optional(),
          count: z.number().int().nonnegative().optional()
        })
      ])
    )
    .optional()
})

export type CreditedMetricEvidenceContribution = {
  accrualDate: string
  attributionKey: string
  convertedAt?: string | null
  conversionKind: 'converted' | 'zero' | 'fallback'
  creditSetupRuleId: number
  assignmentId: number
  metricValueId: number
  originalValue?: number | null
  selectedValue: number
  currency?: string | null
  targetCurrency?: string | null
  exchangeRate?: number | null
}

export type CreditedTargetEvidence = {
  consumerKind: 'target'
  targetId: number
  metricId: number
  creditSetupId: number
  creditSetupRuleId: number | null
  assignmentId: number | null
  profileId: string | null
  teamId: number | null
  effectiveFrom: string
  effectiveTo: string
  noRuleApplied: boolean
  contributions: CreditedMetricEvidenceContribution[]
}

export type PayoutsToInsert = {
  node_output: PaymentNodeOutput
  paymentNodeId?: string
  target_ids?: number[]
  metric_ids?: number[]
}

export type PayoutWithSource = Tables<'payouts'> & {
  payout_installments: Tables<'payout_installments'>[]
  executions: Tables<'executions'> | null
  deals: Tables<'deals'> | null
  invoices: InvoicesWithDeals | null
  activities: ActivitiesWithDeals | null
  line_items: LineItemsWithJoins | null
  deal_line_items: DealLineItemsWithJoins | null
  kpis: Tables<'kpis'> | null
  custom_records: Tables<'custom_records'> | null
  compensation_rules: Pick<
    Tables<'compensation_rules'>,
    'deal_resolution_strategy' | 'deal_resolution_config'
  > | null
}

export type PayoutReleaseContext = PayoutWithSource & {
  compensation_rules: Pick<
    Tables<'compensation_rules'>,
    'source' | 'deal_resolution_strategy' | 'deal_resolution_config'
  > | null
}

export type PayoutRelease = CompositeTypes<'payout_release'>
export type MetricValueToDelete = CompositeTypes<'metric_value_to_delete'>

export interface CSVConfig {
  delimiter: string
  thousandSeparator: string
  decimalSeparator: string
  dateFormat: DateFormatsType
  uniqueKey?: string
  useComposedKey?: boolean
  composedKey?: string[]
  composedKeySequence?: boolean
  orderBy?: string[]
}

export type CSVUploadInfo = {
  tableName: GridTable
  config: CSVConfig | null
  mapping: Record<string, ColumnDefWithMappedValue>
  mappingChanged?: boolean
  headers: string[]
}

export enum CSVImportTaskSteps {
  PREPARE = 'prepare',
  IMPORT = 'import'
}

export enum ImportStatuses {
  INIT = 'init',
  SAVING_FILE_CONFIG_AND_MAPPING = 'saving_file_config_and_mapping',
  WRITING_FILES = 'writing_files',
  LOADING_DATA = 'loading_data',
  MAPPING_IDENTIFIERS = 'mapping_identifiers',
  SAVING_DATA_FOR_REVIEW = 'saving_for_review',
  SAVING_TO_DB = 'saving_to_db',
  FINISHED = 'finished',
  ERROR = 'error'
}

export type RowByTableName<T extends GridTable> = T extends 'deals'
  ? Tables<'deals'>
  : T extends 'invoices'
    ? Tables<'invoices'>
    : T extends 'activities'
      ? Tables<'activities'>
      : T extends 'kpis'
        ? Tables<'kpis'>
        : T extends 'line_items'
          ? Tables<'line_items'>
          : never

export type GetTriggerTokenQueryParams =
  | {
      runId?: undefined
      taskKey: string
      tags: string | string[]
      enabled?: boolean
    }
  | {
      runId: string
      taskKey?: undefined
      tags?: undefined
      enabled?: boolean
    }

// Deal Resolution Strategy types
// Determines which deal to use for all `deal.*` field accesses in the compensation rule
export type DealResolutionStrategy = 'linked' | 'lookup'

export type DealResolutionConfig = {
  /** Which deal to select when multiple match: 'earliest' or 'latest' by close_date */
  deal_selection: 'earliest' | 'latest'
  /** Optional JsonLogic filters to apply to matching deals */
  deal_filters?: Record<string, unknown> | null
}
